import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authenticatedFetch } from '../lib/api';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  DISPLAY_SETTINGS_KEY,
  formatWithDatePattern,
  formatWithTimePattern,
  isDateFormatOption,
  isTimeFormatOption,
  persistDateTimeFormats,
  readStoredDateTimeFormats,
  type DateFormatOption,
  type TimeFormatOption,
} from '../lib/date-format';
import { useAuth } from './AuthContext';

interface DateFormatContextType {
  dateFormat: DateFormatOption;
  timeFormat: TimeFormatOption;
  setDateFormat: (format: string) => void;
  setTimeFormat: (format: string) => void;
  setDateTimeFormats: (dateFormat: string, timeFormat: string) => void;
  formatDate: (value: string | Date | null | undefined) => string;
  formatTime: (value: string | Date | null | undefined) => string;
  loading: boolean;
}

const DateFormatContext = createContext<DateFormatContextType | undefined>(undefined);

export function DateFormatProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const stored = readStoredDateTimeFormats();
  const [dateFormat, setDateFormatState] = useState<DateFormatOption>(stored.dateFormat);
  const [timeFormat, setTimeFormatState] = useState<TimeFormatOption>(stored.timeFormat);
  const [loading, setLoading] = useState(false);

  const applyFormats = useCallback((nextDate: string, nextTime: string) => {
    const date = isDateFormatOption(nextDate) ? nextDate : DEFAULT_DATE_FORMAT;
    const time = isTimeFormatOption(nextTime) ? nextTime : DEFAULT_TIME_FORMAT;
    setDateFormatState(date);
    setTimeFormatState(time);
    persistDateTimeFormats(date, time);
  }, []);

  const setDateFormat = useCallback(
    (format: string) => {
      applyFormats(format, timeFormat);
    },
    [applyFormats, timeFormat]
  );

  const setTimeFormat = useCallback(
    (format: string) => {
      applyFormats(dateFormat, format);
    },
    [applyFormats, dateFormat]
  );

  const setDateTimeFormats = useCallback(
    (nextDate: string, nextTime: string) => {
      applyFormats(nextDate, nextTime);
    },
    [applyFormats]
  );

  // Hydrate from API when authenticated only if not already persisted locally
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;

    try {
      const existingDate = localStorage.getItem('admin_date_format');
      const existingTime = localStorage.getItem('admin_time_format');
      if (existingDate && existingTime) return;
    } catch {}

    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch('/api/v1/auth/preferences');
        if (!response.ok) return;
        const result = await response.json().catch(() => ({}));
        const prefs = (result?.data ?? result ?? {}) as {
          dateFormat?: string;
          timeFormat?: string;
        };
        if (cancelled) return;
        if (prefs.dateFormat || prefs.timeFormat) {
          applyFormats(
            prefs.dateFormat || dateFormat,
            prefs.timeFormat || timeFormat
          );
        }
      } catch {
        // keep local values
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only re-run on auth; avoid looping on format changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, applyFormats]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== DISPLAY_SETTINGS_KEY || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue) as {
          dateFormat?: string;
          timeFormat?: string;
        };
        if (isDateFormatOption(parsed.dateFormat)) setDateFormatState(parsed.dateFormat);
        if (isTimeFormatOption(parsed.timeFormat)) setTimeFormatState(parsed.timeFormat);
      } catch {
        // ignore
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const formatDate = useCallback(
    (value: string | Date | null | undefined) => formatWithDatePattern(value, dateFormat),
    [dateFormat]
  );

  const formatTime = useCallback(
    (value: string | Date | null | undefined) => formatWithTimePattern(value, timeFormat),
    [timeFormat]
  );

  const value = useMemo(
    () => ({
      dateFormat,
      timeFormat,
      setDateFormat,
      setTimeFormat,
      setDateTimeFormats,
      formatDate,
      formatTime,
      loading,
    }),
    [
      dateFormat,
      timeFormat,
      setDateFormat,
      setTimeFormat,
      setDateTimeFormats,
      formatDate,
      formatTime,
      loading,
    ]
  );

  return <DateFormatContext.Provider value={value}>{children}</DateFormatContext.Provider>;
}

export function useDateFormat(): DateFormatContextType {
  const context = useContext(DateFormatContext);
  if (!context) {
    const stored = readStoredDateTimeFormats();
    return {
      dateFormat: stored.dateFormat,
      timeFormat: stored.timeFormat,
      setDateFormat: () => undefined,
      setTimeFormat: () => undefined,
      setDateTimeFormats: () => undefined,
      formatDate: (v) => formatWithDatePattern(v, stored.dateFormat),
      formatTime: (v) => formatWithTimePattern(v, stored.timeFormat),
      loading: false,
    };
  }
  return context;
}
